import { deleteTypingTestLanguageCookie, setTypingTestLanguageCookie } from 'api-next/cookie';
import React, { useCallback, useMemo } from 'react';

import useTypingStore from '@Root/src/store/useTypingStore';

import { InputSelectStyle, SingleOption } from '@Components/atoms/Form/InputSelect/InputSelect';
import { PropsWithClassName } from '@Components/helper';
import { DEFAULT_LANGUAGE_ISO } from '@Components/helper/language';
import { getLanguageNameByIso, getLanguageOptionByIso } from '@Components/helper/languageJson';
import { useBreakpoint } from '@Components/helper/useBreakpoint';
import { LanguageDropdown } from '@Components/molecules/LanguageDropdown/LanguageDropdown';

import { Root } from './LanguageDropdownState.styles';

interface Props extends PropsWithClassName {
  initialLanguageIso: string;
}

const LanguageDropdownState = (props: Props): React.ReactElement => {
  const { initialLanguageIso } = props;
  const setLanguage = useTypingStore((state) => state.setLanguage);
  const languageIso = useTypingStore((state) => state.settings.languageIso);

  const { isMobile } = useBreakpoint();

  const language = getLanguageOptionByIso(languageIso || initialLanguageIso, isMobile);

  const updateTypingTestLanguageCookie = useCallback(async (iso: string) => {
    if (iso === DEFAULT_LANGUAGE_ISO) {
      await deleteTypingTestLanguageCookie();

      return;
    }

    const languageName = getLanguageNameByIso(iso, false);
    if (languageName) {
      await setTypingTestLanguageCookie(languageName);
    }
  }, []);

  const onChange = useCallback(
    async (newValue: SingleOption) => {
      setLanguage(newValue.value, newValue.label);
      await updateTypingTestLanguageCookie(newValue.value);
    },
    [setLanguage, updateTypingTestLanguageCookie]
  );

  return useMemo(
    () => (
      <Root>
        <LanguageDropdown
          language={language!}
          onChange={onChange}
          styleType={InputSelectStyle.Minimal}
          dataTestId={props.dataTestId}
        />
      </Root>
    ),
    [language, onChange, props.dataTestId]
  );
};

export default LanguageDropdownState;
